/**
 * Арифметика прогресса: доли, две оси, три знаменателя.
 *
 * Живёт в `lib`, а не в разделе, потому что этих экранов теперь два —
 * карантин и кампании — и они считают ОДНО И ТО ЖЕ. Вторая копия правила
 * разошлась бы с первой тихо: оба ответа выглядят правдоподобно, и заметить
 * расхождение можно только сравнив два экрана вручную.
 *
 * Правила, ради которых модуль существует:
 *
 * * **нулевой знаменатель не даёт нуля процентов** — «0 из 0» это не «0%», а
 *   отсутствие предмета счёта. Такая доля возвращается как `null`;
 * * **отсутствующая площадь не превращается в ноль** — гектарная доля
 *   считается только когда сервер прислал ОБА числа;
 * * **«отчитался» и «проверено» — две доли, а не одна** — вторая никогда не
 *   выводится из первой.
 */

import type { ProgressArea, ProgressCount } from "../api/client";
import { defineMessages } from "../i18n/define";
import { tr } from "../i18n/runtime";

const M = defineMessages({
  obligations: { id: "common.progress.obligations", defaultMessage: "Работы на полях", description: "Название счёта «работ на полях» в блоке трёх знаменателей прогресса (работа × контур)" },
  contours: { id: "common.progress.contours", defaultMessage: "Контуры", description: "Название счёта контуров в блоке трёх знаменателей прогресса" },
  actors: { id: "common.progress.actors", defaultMessage: "Хозяйства", description: "Название счёта хозяйств в блоке трёх знаменателей прогресса" },
  noDeadlineLabel: { id: "common.progress.window.none.label", defaultMessage: "без срока", description: "Короткая подпись окна работы: срока нет" },
  noDeadlineHint: { id: "common.progress.window.none.hint", defaultMessage: "У работы нет срока — просрочки по ней не существует.", description: "Подсказка к окну работы без срока" },
  runningLabel: { id: "common.progress.window.running.label", defaultMessage: "окно идёт", description: "Короткая подпись окна работы: окно ещё не закончилось" },
  runningHint: { id: "common.progress.window.running.hint", defaultMessage: "Окно ещё не закончилось: отсутствие отчёта означает «данных нет», а не «не выполнено».", description: "Подсказка к идущему окну работы: почему отсутствие отчёта пока не нарушение" },
  elapsedLabel: { id: "common.progress.window.elapsed.label", defaultMessage: "окно закончилось", description: "Короткая подпись окна работы: срок вышел" },
});

export interface Share {
  /** Доля от 0 до 1. `null` — считать не от чего. */
  value: number | null;
  /** Целые проценты для подписи. `null` — доли нет. */
  percent: number | null;
}

const NO_SHARE: Share = { value: null, percent: null };

/**
 * Доля «часть от целого». Пустой или отсутствующий знаменатель даёт `null`:
 * «0 из 0» — это не ноль процентов, а отсутствие предмета счёта.
 */
export function share(
  part: number | null | undefined,
  total: number | null | undefined,
): Share {
  if (part === null || part === undefined) return NO_SHARE;
  if (total === null || total === undefined || total <= 0) return NO_SHARE;
  const value = part / total;
  return { value, percent: Math.round(value * 100) };
}

export interface ProgressView {
  label: string;
  total: number;
  reported: number;
  verified: number;
  reportedShare: Share;
  verifiedShare: Share;
}

export function progressView(label: string, counts: ProgressCount): ProgressView {
  return {
    label,
    total: counts.total,
    reported: counts.reported,
    verified: counts.verified,
    reportedShare: share(counts.reported, counts.total),
    verifiedShare: share(counts.verified, counts.total),
  };
}

export interface AreaView {
  totalMicroha: number | null;
  reportedMicroha: number | null;
  verifiedMicroha: number | null;
  missingUnits: number;
  /**
   * Контуры, заявленные и целиком, и своими частями. Их гектары уже посчитаны
   * ОДИН раз: сервер сворачивает часть в её контур и не складывает контур с
   * его частями. Число остаётся на экране не как поправка к сумме, а как
   * замечание к данным — реестр заявляет эту землю дважды, и какая из двух
   * строк верна, решает владелец, а не интерфейс.
   */
  overlappingUnits: number;
  reportedShare: Share;
  verifiedShare: Share;
}

export function areaView(area: ProgressArea): AreaView {
  return {
    totalMicroha: area.total_microha,
    reportedMicroha: area.reported_microha,
    verifiedMicroha: area.verified_microha,
    missingUnits: area.missing_units,
    overlappingUnits: area.overlapping_units ?? 0,
    reportedShare: share(area.reported_microha, area.total_microha),
    verifiedShare: share(area.verified_microha, area.total_microha),
  };
}

/**
 * Три знаменателя одной работы, снизу вверх: работы на полях → контуры →
 * хозяйства. Отдаются вместе и всегда: у хозяйства с одним крупным контуром и
 * у хозяйства с двадцатью мелкими «50% по контурам» означают разный объём
 * работы, и один процент не заменяет другой.
 */
export function denominatorsOf(source: {
  obligations: ProgressCount;
  contours: ProgressCount;
  actors: ProgressCount;
}): ProgressView[] {
  return [
    progressView(tr(M.obligations), source.obligations),
    progressView(tr(M.contours), source.contours),
    progressView(tr(M.actors), source.actors),
  ];
}

export interface WindowNote {
  /** Короткая подпись состояния окна. */
  label: string;
  /** Почему отсутствие отчётов здесь не является нарушением. */
  hint: string | null;
}

/**
 * Что означает окно работы. У идущего окна отсутствие отчёта — «ещё не срок»,
 * и подпись обязана это сказать: иначе идущая кампания читалась бы как
 * проваленная.
 */
export function windowNote(source: {
  window_to: string | null;
  window_elapsed?: boolean | null;
}): WindowNote {
  if (source.window_to === null) {
    return {
      label: tr(M.noDeadlineLabel),
      hint: tr(M.noDeadlineHint),
    };
  }
  if (source.window_elapsed === false) {
    return {
      label: tr(M.runningLabel),
      hint: tr(M.runningHint),
    };
  }
  return { label: tr(M.elapsedLabel), hint: null };
}
